import { computed, getCurrentInstance, ref } from '@vue/composition-api';
import Api from '../Api';

export const useBilling = () => {
  const vm = getCurrentInstance();
  const store = vm.proxy.$store;
  
  // getters
  const autoPayDetails = computed(() => store.getters['autoPayDetails']);
  const autoPayDetailsLoading = computed(() => store.getters['autoPayDetailsLoading']);
  const autoPayEnabled = computed(() => store.getters['autoPayEnabled']);
  const cashOnAccountsLoading = computed(() => store.getters['cashOnAccountsLoading']);
  const currentBalanceDue = computed(() => store.getters['currentBalanceDue']);
  const currentPaymentMethod = computed(() => store.getters['currentPaymentMethod']);
  const cybersourceDetails = computed(() => store.getters['cybersourceDetails']);
  const cybersourceDetailsLoading = computed(() => store.getters['cybersourceDetailsLoading']);
  const deletingUserPaymentMethod = computed(() => store.getters['deletingUserPaymentMethod']);
  const filteredInvoices = computed(() => store.getters['filteredInvoices']);
  const hasUserPaymentMethods = computed(() => store.getters['hasUserPaymentMethods']);
  const hasUserPaymentMethodsByLob = computed(() => store.getters['hasUserPaymentMethodsByLob']);
  const invoicePaymentHistory = computed(() => store.getters['invoicePaymentHistory']);
  const invoicePaymentHistoryLoading = computed(() => store.getters['invoicePaymentHistoryLoading']);
  const invoicePaymentHistoryUserMessage = computed(() => store.getters['invoicePaymentHistoryUserMessage']);
  const invoices = computed(() => store.getters['invoices']);
  const invoicesLoading = computed(() => store.getters['invoicesLoading']);
  const invoicesUserMessage = computed(() => store.getters['invoicesUserMessage']);
  const isBlocking = computed(() => store.getters['isBlocking']);
  const isUnBlocking = computed(() => store.getters['isUnBlocking']);
  const lastPaymentMade = computed(() => store.getters['lastPaymentMade']);
  const lobId = computed(() => store.getters['lobId']);
  const makingPayment = computed(() => store.getters['makingPayment']);
  const onetimePaymentDetails = computed(() => store.getters['onetimePaymentDetails']);
  const payment = computed(() => store.getters['payment']);
  const paymentHistory = computed(() => store.getters['paymentHistory']);
  const paymentHistoryConfigurationLoading = computed(() => store.getters['paymentHistoryConfigurationLoading']);
  const paymentHistoryEnabled = computed(() => store.getters['paymentHistoryEnabled']);
  const paymentHistoryLoading = computed(() => store.getters['paymentHistoryLoading']);
  const paymentInvoices = computed(() => store.getters['paymentInvoices']);
  const paymentLobs = computed(() => store.getters['paymentLobs']);
  const paymentMethods = computed(() => store.getters['paymentMethods']);
  const paymentMethodsLoading = computed(() => store.getters['paymentMethodsLoading']);
  const paymentsAvailable = computed(() => store.getters['paymentsAvailable']);
  const paymentsAvailableForSubGroup = computed(() => store.getters['paymentsAvailableForSubGroup']);
  const recurringPaymentsForm = computed(() => store.getters['recurringPaymentsForm']);
  const singleInvoice = computed(() => store.getters['singleInvoice']);
  const singleInvoiceLoading = computed(() => store.getters['singleInvoiceLoading']);
  const subGroupDetails = computed(() => store.getters['subGroupDetails']);
  const subGroupDetailsLoading = computed(() => store.getters['subGroupDetailsLoading']);
  const updateDefaultPaymentMethodLoading = computed(() => store.getters['updateDefaultPaymentMethodLoading']);
  const userPaymentMethods = computed(() => store.getters['userPaymentMethods']);
  const userPaymentMethodsByLob = computed(() => store.getters['userPaymentMethodsByLob'] || []);
  const userPaymentMethodsLoading = computed(() => store.getters['userPaymentMethodsLoading']);

  // actions
  const BLOCK_INVOICE = (opts) => store.dispatch('BLOCK_INVOICE', opts);
  const BLOCK_INVOICES = (opts) => store.dispatch('BLOCK_INVOICES', opts);
  const DELETE_PAYMENT_METHOD = (opts) => store.dispatch('DELETE_PAYMENT_METHOD', opts);
  const DELETE_USER_PAYMENT_METHOD = (opts) => store.dispatch('DELETE_USER_PAYMENT_METHOD', opts);
  const ENFORCE_INVOICE_PORTAL_CONTEXT = (opts) => store.dispatch('ENFORCE_INVOICE_PORTAL_CONTEXT', opts);
  const GET_AUTO_PAY_DETAILS = (opts) => store.dispatch('GET_AUTO_PAY_DETAILS', opts);
  const GET_CASH_ON_ACCOUNTS = (opts) => store.dispatch('GET_CASH_ON_ACCOUNTS', opts);
  const GET_CYBERSOURCE_DETAILS = (opts) => store.dispatch('GET_CYBERSOURCE_DETAILS', opts);
  const GET_CYBERSOURCE_ONETIME_PAYMENT_DETAILS = (opts) => store.dispatch('GET_CYBERSOURCE_ONETIME_PAYMENT_DETAILS', opts);
  const GET_INVOICE_PAYMENT_HISTORY = (opts) => store.dispatch('GET_INVOICE_PAYMENT_HISTORY', opts);
  const GET_INVOICES = (opts) => store.dispatch('GET_INVOICES', opts);
  const GET_PAYMENT_HISTORY = (opts) => store.dispatch('GET_PAYMENT_HISTORY', opts);
  const GET_PAYMENT_HISTORY_CONFIGURATION = (opts) => store.dispatch('GET_PAYMENT_HISTORY_CONFIGURATION', opts);
  const GET_PAYMENT_LOBS = (opts) => store.dispatch('GET_PAYMENT_LOBS', opts);
  const GET_PAYMENT_METHODS = (opts) => store.dispatch('GET_PAYMENT_METHODS', opts);
  const GET_SINGLE_INVOICE = (opts) => store.dispatch('GET_SINGLE_INVOICE', opts);
  const GET_SUBGROUP_DETAILS = (opts) => store.dispatch('GET_SUBGROUP_DETAILS', opts);
  const GET_USER_PAYMENT_METHODS = (opts) => store.dispatch('GET_USER_PAYMENT_METHODS', opts);
  const MAKE_PAYMENT = (opts) => store.dispatch('MAKE_PAYMENT', opts);
  const SETUP_RECURRING_PAYMENTS = (opts) => store.dispatch('SETUP_RECURRING_PAYMENTS', opts);
  const UNBLOCK_ALL_INVOICES = (opts) => store.dispatch('UNBLOCK_ALL_INVOICES', opts);
  const UNBLOCK_INVOICE = (opts) => store.dispatch('UNBLOCK_INVOICE', opts);
  const UNBLOCK_INVOICES = (opts) => store.dispatch('UNBLOCK_INVOICES', opts);
  const UPDATE_DEFAULT_USER_PAYMENT_METHOD = (opts) => store.dispatch('UPDATE_DEFAULT_USER_PAYMENT_METHOD', opts);

  // mutations
  const BEGIN_BLOCKING_INVOICES = (data) => store.commit('BEGIN_BLOCKING_INVOICES', data);
  const BEGIN_LOADING_INVOICES = (data) => store.commit('BEGIN_LOADING_INVOICES', data);
  const BEGIN_LOADING_PAYMENT_HISTORY = (data) => store.commit('BEGIN_LOADING_PAYMENT_HISTORY', data);
  const BEGIN_UNBLOCKING_INVOICES = (data) => store.commit('BEGIN_UNBLOCKING_INVOICES', data);
  const CLEAR_BILLING = (data) => store.commit('CLEAR_BILLING', data);
  const CLEAR_BILLING_DETAILS_WITH_PORTAL_CONTEXT = (data) => store.commit('CLEAR_BILLING_DETAILS_WITH_PORTAL_CONTEXT', data);
  const CONFIRM_PAYMENT = (data) => store.commit('CONFIRM_PAYMENT', data);
  const SET_AUTO_PAY_DETAILS = (data) => store.commit('SET_AUTO_PAY_DETAILS', data);
  const SET_CASH_ON_ACCOUNTS = (data) => store.commit('SET_CASH_ON_ACCOUNTS', data);
  const SET_CASH_ON_ACCOUNTS_LOADING = (data) => store.commit('SET_CASH_ON_ACCOUNTS_LOADING', data);
  const SET_CONFIGURING_RECURRING_PAYMENTS = (data) => store.commit('SET_CONFIGURING_RECURRING_PAYMENTS', data);
  const SET_CYBERSOURCE_DETAILS = (data) => store.commit('SET_CYBERSOURCE_DETAILS', data);
  const SET_DELETING_PAYMENT_METHOD = (data) => store.commit('SET_DELETING_PAYMENT_METHOD', data);
  const SET_DELETING_USER_PAYMENT_METHOD = (data) => store.commit('SET_DELETING_USER_PAYMENT_METHOD', data);
  const SET_FILTERED_INVOICES = (data) => store.commit('SET_FILTERED_INVOICES', data);
  const SET_INVOICE_PAYMENT_HISTORY = (data) => store.commit('SET_INVOICE_PAYMENT_HISTORY', data);
  const SET_INVOICE_PAYMENT_HISTORY_LOADING = (data) => store.commit('SET_INVOICE_PAYMENT_HISTORY_LOADING', data);
  const SET_INVOICE_PAYMENT_HISTORY_USER_MESSAGE = (data) => store.commit('SET_INVOICE_PAYMENT_HISTORY_USER_MESSAGE', data);
  const SET_INVOICES = (data) => store.commit('SET_INVOICES', data);
  const SET_INVOICES_USER_MESSAGE = (data) => store.commit('SET_INVOICES_USER_MESSAGE', data);
  const SET_LOADING_CYBERSOURCE_DETAILS = (data) => store.commit('SET_LOADING_CYBERSOURCE_DETAILS', data);
  const SET_LOADING_PAYMENT_METHODS = (data) => store.commit('SET_LOADING_PAYMENT_METHODS', data);
  const SET_LOADING_USER_PAYMENT_METHODS = (data) => store.commit('SET_LOADING_USER_PAYMENT_METHODS', data);
  const SET_MAKING_PAYMENT = (data) => store.commit('SET_MAKING_PAYMENT', data);
  const SET_ONETIME_PAYMENT_VARIABLES = (data) => store.commit('SET_ONETIME_PAYMENT_VARIABLES', data);
  const SET_PAYMENT_HISTORY = (data) => store.commit('SET_PAYMENT_HISTORY', data);
  const SET_PAYMENT_HISTORY_CONFIGURATION_LOADING = (data) => store.commit('SET_PAYMENT_HISTORY_CONFIGURATION_LOADING', data);
  const SET_PAYMENT_HISTORY_ENABLED = (data) => store.commit('SET_PAYMENT_HISTORY_ENABLED', data);
  const SET_PAYMENT_LOBS = (data) => store.commit('SET_PAYMENT_LOBS', data);
  const SET_PAYMENT_METHODS = (data) => store.commit('SET_PAYMENT_METHODS', data);
  const SET_SINGLE_INVOICE = (data) => store.commit('SET_SINGLE_INVOICE', data);
  const SET_SINGLE_INVOICE_ERROR = (data) => store.commit('SET_SINGLE_INVOICE_ERROR', data);
  const SET_SUBGROUP_DETAILS = (data) => store.commit('SET_SUBGROUP_DETAILS', data);
  const SET_SUBGROUP_DETAILS_LOADING = (data) => store.commit('SET_SUBGROUP_DETAILS_LOADING', data);
  const SET_USER_PAYMENT_METHODS = (data) => store.commit('SET_USER_PAYMENT_METHODS', data);
  const TOGGLE_BLOCKED_STATE_OF_SINGLE_INVOICE = (data) => store.commit('TOGGLE_BLOCKED_STATE_OF_SINGLE_INVOICE', data);
  const UPDATE_DEFAULT_USER_PAYMENT_METHOD_LOADING = (data) => store.commit('UPDATE_DEFAULT_USER_PAYMENT_METHOD_LOADING', data);

  const addPaymentMethodToCybersource = async (url, opts) => {
    console.log(`url`, url, 'opts', opts);
    new Api().post(url, opts).then(() => console.log(`posted`)).catch((e) => console.log(`error postin`, e));
  }

  return {
    addPaymentMethodToCybersource,
    autoPayDetails,
    autoPayDetailsLoading,
    autoPayEnabled,
    cashOnAccountsLoading,
    currentBalanceDue,
    currentPaymentMethod,
    cybersourceDetails,
    cybersourceDetailsLoading,
    deletingUserPaymentMethod,
    filteredInvoices,
    hasUserPaymentMethods,
    hasUserPaymentMethodsByLob,
    invoicePaymentHistory,
    invoicePaymentHistoryLoading,
    invoicePaymentHistoryUserMessage,
    invoices,
    invoicesLoading,
    invoicesUserMessage,
    isBlocking,
    isUnBlocking,
    lastPaymentMade,
    lobId,
    makingPayment,
    onetimePaymentDetails,
    payment,
    paymentHistory,
    paymentHistoryConfigurationLoading,
    paymentHistoryEnabled,
    paymentHistoryLoading,
    paymentInvoices,
    paymentLobs,
    paymentMethods,
    paymentMethodsLoading,
    paymentsAvailable,
    paymentsAvailableForSubGroup,
    recurringPaymentsForm,
    singleInvoice,
    singleInvoiceLoading,
    subGroupDetails,
    subGroupDetailsLoading,
    updateDefaultPaymentMethodLoading,
    userPaymentMethods,
    userPaymentMethodsByLob,
    userPaymentMethodsLoading,

    BEGIN_BLOCKING_INVOICES,
    BEGIN_LOADING_INVOICES,
    BEGIN_LOADING_PAYMENT_HISTORY,
    BEGIN_UNBLOCKING_INVOICES,
    BLOCK_INVOICE,
    BLOCK_INVOICES,
    CLEAR_BILLING,
    CLEAR_BILLING_DETAILS_WITH_PORTAL_CONTEXT,
    CONFIRM_PAYMENT,
    DELETE_PAYMENT_METHOD,
    DELETE_USER_PAYMENT_METHOD,
    ENFORCE_INVOICE_PORTAL_CONTEXT,
    GET_AUTO_PAY_DETAILS,
    GET_CASH_ON_ACCOUNTS,
    GET_CYBERSOURCE_DETAILS,
    GET_CYBERSOURCE_ONETIME_PAYMENT_DETAILS,
    GET_INVOICE_PAYMENT_HISTORY,
    GET_INVOICES,
    GET_PAYMENT_HISTORY,
    GET_PAYMENT_HISTORY_CONFIGURATION,
    GET_PAYMENT_LOBS,
    GET_PAYMENT_METHODS,
    GET_SINGLE_INVOICE,
    GET_SUBGROUP_DETAILS,
    GET_USER_PAYMENT_METHODS,
    MAKE_PAYMENT,
    SET_AUTO_PAY_DETAILS,
    SET_CASH_ON_ACCOUNTS,
    SET_CASH_ON_ACCOUNTS_LOADING,
    SET_CONFIGURING_RECURRING_PAYMENTS,
    SET_CYBERSOURCE_DETAILS,
    SET_DELETING_PAYMENT_METHOD,
    SET_DELETING_USER_PAYMENT_METHOD,
    SET_FILTERED_INVOICES,
    SET_INVOICE_PAYMENT_HISTORY,
    SET_INVOICE_PAYMENT_HISTORY_LOADING,
    SET_INVOICE_PAYMENT_HISTORY_USER_MESSAGE,
    SET_INVOICES,
    SET_INVOICES_USER_MESSAGE,
    SET_LOADING_CYBERSOURCE_DETAILS,
    SET_LOADING_PAYMENT_METHODS,
    SET_LOADING_USER_PAYMENT_METHODS,
    SET_MAKING_PAYMENT,
    SET_ONETIME_PAYMENT_VARIABLES,
    SET_PAYMENT_HISTORY,
    SET_PAYMENT_HISTORY_CONFIGURATION_LOADING,
    SET_PAYMENT_HISTORY_ENABLED,
    SET_PAYMENT_LOBS,
    SET_PAYMENT_METHODS,
    SET_SINGLE_INVOICE,
    SET_SINGLE_INVOICE_ERROR,
    SET_SUBGROUP_DETAILS,
    SET_SUBGROUP_DETAILS_LOADING,
    SET_USER_PAYMENT_METHODS,
    SETUP_RECURRING_PAYMENTS,
    TOGGLE_BLOCKED_STATE_OF_SINGLE_INVOICE,
    UNBLOCK_ALL_INVOICES,
    UNBLOCK_INVOICE,
    UNBLOCK_INVOICES,
    UPDATE_DEFAULT_USER_PAYMENT_METHOD,
    UPDATE_DEFAULT_USER_PAYMENT_METHOD_LOADING,
  }
}